import { createSelector } from "reselect";
import type { RootState } from "~/app/store";
import {
  type ExpandedResolvedWishlistEntryModel,
  type ResolvedWishlistEntryModel,
  type WishlistEntryModel,
  type WishlistModel,
} from "~/features/wishlist/types/model";
import { type SelectNetPriceProps } from "~/domains/product/utils/productDescriptorUtils";
import { getProductDescriptorMap, selectDeepProductDescriptor } from "~/domains/product/redux/selectors";
import { getCanUserAddToCart } from "~/features/productDetails/components/ProductSummary/ExtendedPdpSummary";
import { userIsRegistered } from "~/domains/user/utils/roles";
import {
  selectCachedWishlists,
  selectCachedWishlistsMap,
  selectCachedEntriesMap,
  selectIsWishlistsError as selectIsWishlistsErrorFromSlice,
  selectIsWishlistsLoading as selectIsWishlistsLoadingFromSlice,
} from "./wishlistSlice";

// ============================================================================
// Core Selectors - Data from RTK Query cache
// ============================================================================

/** Are wishlists loading (initial fetch or refetch)? */
export const selectIsWishlistsLoading = selectIsWishlistsLoadingFromSlice;

/** Did the wishlist fetch fail? */
export const selectIsWishlistsError = selectIsWishlistsErrorFromSlice;

/** Get all wishlists */
export const selectWishlists = selectCachedWishlists;

/** Get selected wishlist ID from UI state */
export const selectSelectedWishlistId = (state: RootState): string | null => state.wishlistUI.selectedWishlistId;

/** Get the selected wishlist object */
export const selectSelectedWishlist = createSelector(
  selectSelectedWishlistId,
  selectCachedWishlistsMap,
  (wishlistId, wishlistsMap): WishlistModel | null => {
    if (!wishlistId) {
      return null;
    }
    return wishlistsMap[wishlistId] ?? null;
  },
);

/** Get entries for the selected wishlist */
export const selectSelectedWishlistEntries = createSelector(
  selectSelectedWishlist,
  selectCachedEntriesMap,
  (wishlist, entriesMap): WishlistEntryModel[] => {
    if (!wishlist) {
      return [];
    }
    return wishlist.entryIds.map((id) => entriesMap[id]).filter((e): e is WishlistEntryModel => !!e);
  },
);

/** Get wishlist by ID */
export const selectWishlistById = (state: RootState, wishlistId: string): WishlistModel | undefined => {
  return selectCachedWishlistsMap(state)[wishlistId];
};

/** Get size of a specific wishlist */
export const selectWishlistSize = (wishlistId: string) => (state: RootState) => {
  return selectWishlistById(state, wishlistId)?.entryIds.length ?? 0;
};

/** Total number of wishlists */
export const selectWishlistAmount = (state: RootState) => selectCachedWishlists(state).length;

import type { LoadableEntry } from "~/app/store/loadableTypes";
import type { PriceInfo } from "~/domains/pricing/api/PriceInfo";
import { getFirstValidPackCode } from "../components/modals/AddToWishlist/utils";

// ============================================================================
// Derived Selectors
// ============================================================================

export const isAllWishlistDetailsLoaded = createSelector(
  (state: RootState) => state.products.price,
  (priceMap: Record<string, LoadableEntry<PriceInfo[]>>) => {
    return Object.values(priceMap).every((entry: LoadableEntry<PriceInfo[]>) => entry.state === "loaded");
  },
);

export const getSelectPriceParamsFromWishlistEntry = (
  wishlistEntry: WishlistEntryModel | ResolvedWishlistEntryModel,
  packCode: string,
): Required<SelectNetPriceProps> => {
  return {
    packCode,
    extraQuantity: wishlistEntry.quantity,
    priceReference: wishlistEntry.priceReference,
    productCode: "productCode" in wishlistEntry ? wishlistEntry.productCode : wishlistEntry.product.base.code,
  };
};

/** Get fully resolved wishlist with product descriptors and UI state */
export const selectFullWishlist = createSelector(
  (state: RootState, wishlistId: string) => selectWishlistById(state, wishlistId),
  selectCachedEntriesMap,
  (state: RootState) => state.wishlistUI.entryUIState,
  (state: RootState) => state.products,
  (wishlistModel, entriesMap, entryUIState, productStore) => {
    if (!wishlistModel) {
      return null;
    }
    return {
      ...wishlistModel,
      entries: wishlistModel.entryIds
        .map((id: string): ExpandedResolvedWishlistEntryModel | null => {
          const entry = entriesMap[id];
          if (!entry) {
            return null;
          }
          const uiState = entryUIState[id];
          const product = selectDeepProductDescriptor(productStore, entry);
          return {
            ...entry,
            product,
            packCode: getFirstValidPackCode(product) ?? uiState?.packCode ?? "",
            isSaleEnabled: uiState?.isSaleEnabled ?? false,
            isSelectable: userIsRegistered()
              ? true
              : product.isLoaded && getCanUserAddToCart(product, product?.availability?.packagingInformation[0]),
          };
        })
        .filter((e: ExpandedResolvedWishlistEntryModel | null): e is ExpandedResolvedWishlistEntryModel => !!e),
    };
  },
);

/** Check if "Add All to Cart" should show loading message */
export const selectShowATCNotReadyMsg = (state: RootState) => {
  const wishlistEntries = selectSelectedWishlistEntries(state);
  const productsMap = getProductDescriptorMap(state, { wishlistEntries });

  return state.wishlistUI.addAllClicked && wishlistEntries.some((entry) => productsMap[entry.productCode]?.isLoading);
};

/** Check if "Add All to Cart" should show error message */
export const selectShowWSErrorMsg = (state: RootState) => {
  const wishlistEntries = selectSelectedWishlistEntries(state);
  const productsMap = getProductDescriptorMap(state, { wishlistEntries });

  return state.wishlistUI.addAllClicked && wishlistEntries.some((entry) => productsMap[entry.productCode]?.isError);
};

/** Check if the selected wishlist has been processed (details fetched) */
export const selectIsSelectedWishlistProcessed = (state: RootState) => {
  const selectedId = state.wishlistUI.selectedWishlistId;
  if (!selectedId) {
    return false;
  }
  return state.wishlistUI.processedWishlistIds.includes(selectedId);
};
